import { gql, useSubscription } from "@apollo/client";
import { AtomStoreContext, DebugSettingsContext } from "@atoms/atom-client";
import { EntryType } from "@atoms/atom-types";
import type React from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { EnhancedProtocolViewer } from "./EnhancedProtocolViewer";
import { ProtocolList } from "./ProtocolList";
import { TimelineOverview } from "./TimelineOverview";
import { useDebugProtocolStore } from "./useDebugProtocolStore";

// Remove button component to avoid direct DOM manipulation
// biome-ignore lint/suspicious/noExplicitAny: legacy code
const RemoveButton: React.FC<{ onClick: () => void; theme: any }> = ({
  onClick,
  theme,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: "0.25rem 0.75rem",
        background: isHovered ? theme.errorColor : "transparent",
        color: isHovered ? "white" : theme.errorColor,
        border: `1px solid ${theme.errorColor}`,
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "0.75rem",
        transition: "all 0.2s",
      }}
    >
      Remove
    </button>
  );
};

interface DebugMenuProps {
  children: React.ReactNode;
}

const DEBUG_PROTOCOL_SUBSCRIPTION = gql`
  subscription DebugProtocol {
    debugProtocolSubscription
  }
`;

export const DebugMenu: React.FC<DebugMenuProps> = ({ children }) => {
  const [visible, setVisible] = useState(false);
  const [enableDataLogging, setEnableDataLogging] = useState(() => {
    return localStorage.getItem("debug_enableDataLogging") === "true";
  });
  const [enableContextPicker, setEnableContextPicker] = useState(() => {
    return localStorage.getItem("debug_enableContextPicker") === "true";
  });
  const [enableStoreInConsole, setEnableStoreInConsole] = useState(() => {
    return localStorage.getItem("debug_enableStoreInConsole") === "true";
  });
  const [enableGraphQLDebug, setEnableGraphQLDebug] = useState(() => {
    return localStorage.getItem("graphqlDebug") === "true";
  });
  const [_showStoredProtocols, _setShowStoredProtocols] = useState(true);
  const [selectedProtocolId, setSelectedProtocolId] = useState<
    string | undefined
  >();
  const [viewMode, setViewMode] = useState<"timeline" | "list">(() => {
    return (
      (localStorage.getItem("debug_viewMode") as "timeline" | "list") || "list"
    );
  });

  // Detect system theme preference
  const [isLightMode, setIsLightMode] = useState(() => {
    const savedTheme = localStorage.getItem("debug_theme");
    if (savedTheme) return savedTheme === "light";
    return window.matchMedia?.("(prefers-color-scheme: light)").matches;
  });

  const atomStore = useContext(AtomStoreContext);
  const apolloClient = atomStore?.getApolloClient();
  if (!apolloClient) throw new Error("No Apollo client found");
  const {
    protocols,
    addProtocol,
    clearProtocols,
    removeProtocol,
    maxProtocols,
    setMaxProtocols,
  } = useDebugProtocolStore();

  // Theme colors
  const theme = {
    background: isLightMode
      ? "rgba(255, 255, 255, 0.95)"
      : "rgba(0, 0, 0, 0.95)",
    text: isLightMode ? "#000000" : "#ffffff",
    headerBg: isLightMode ? "#f5f5f5" : "#111111",
    borderColor: isLightMode ? "#e0e0e0" : "#333333",
    subtextColor: isLightMode ? "#666666" : "#888888",
    cardBg: isLightMode ? "#ffffff" : "#0a0a0a",
    cardHeaderBg: isLightMode ? "#f9f9f9" : "#1a1a1a",
    inputBg: isLightMode ? "#ffffff" : "#222222",
    inputBorder: isLightMode ? "#d0d0d0" : "#555555",
    buttonBg: isLightMode ? "#e0e0e0" : "#333333",
    buttonText: isLightMode ? "#000000" : "#ffffff",
    errorColor: "#dc2626",
    successColor: "#10b981",
    warningColor: "#f59e0b",
    purpleColor: "#8b5cf6",
    blueColor: "#3b82f6",
  };

  // Save theme preference
  useEffect(() => {
    localStorage.setItem("debug_theme", isLightMode ? "light" : "dark");
  }, [isLightMode]);

  const { data, error } = useSubscription(DEBUG_PROTOCOL_SUBSCRIPTION, {
    client: apolloClient,
    skip: !enableDataLogging,
  });

  // Store protocol when received
  useEffect(() => {
    if (data?.debugProtocolSubscription && enableDataLogging) {
      console.log("Debug protocol received:", data.debugProtocolSubscription);
      addProtocol(data.debugProtocolSubscription);
    }
  }, [data?.debugProtocolSubscription, enableDataLogging, addProtocol]);

  // Enhanced console access for debugging
  useEffect(() => {
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    (globalThis as any).debugProtocol = data?.debugProtocolSubscription;
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    (globalThis as any).debugProtocolStore = {
      protocols,
      clearProtocols,
      // Helper functions for debugging
      getForks: () => protocols.filter((p) => p.isFork),
      getNonForks: () => protocols.filter((p) => !p.isFork),
      findByTransactionId: (id: string) =>
        protocols.find((p) => p.transactionId === id),
      findByParentId: (parentId: string) =>
        protocols.filter((p) => p.parentTransactionId === parentId),
      getProtocolTree: () => {
        // biome-ignore lint/suspicious/noExplicitAny: legacy code
        const tree: any = {};
        const roots = protocols.filter((p) => !p.parentTransactionId);
        for (const root of roots) {
          tree[root.transactionId || root.id] = {
            ...root,
            children: protocols.filter(
              (p) => p.parentTransactionId === (root.transactionId || root.id),
            ),
          };
        }
        return tree;
      },
      // Access to the raw store
      store: useDebugProtocolStore.getState(),
      // Fix missing fork data based on operation names
      fixForkData: () => {
        const state = useDebugProtocolStore.getState();
        const updatedProtocols = state.protocols.map((p) => {
          // If already has fork data, skip
          if (p.isFork !== undefined) return p;

          // Check if operation name suggests it's a fork
          const isForkOperation =
            p.operationName.includes("generate-") ||
            p.operationName.includes("slow-") ||
            p.operationName === "generate-suggestion-images" ||
            p.operationName === "generate-suggestions-data" ||
            p.operationName === "generate-recipe-with-ai";

          if (isForkOperation && !p.isFork) {
            console.log(`Fixing fork data for: ${p.operationName}`);
            return { ...p, isFork: true };
          }

          return p;
        });

        // Update the store
        useDebugProtocolStore.setState({ protocols: updatedProtocols });
        console.log(
          "Fork data fixed for",
          updatedProtocols.filter((p) => p.isFork).length,
          "protocols",
        );
      },
      // Infer parent-child relationships based on timing
      inferRelationships: () => {
        const state = useDebugProtocolStore.getState();
        const protocols = [...state.protocols];

        // Sort by timestamp
        protocols.sort((a, b) => a.timestamp - b.timestamp);

        const updated = protocols.map((protocol) => {
          // Skip if already has parent
          if (protocol.parentTransactionId) return protocol;

          // If it's a fork, try to find parent
          if (protocol.isFork) {
            // Look for non-fork protocols that ended within 10 seconds before this fork started
            const potentialParents = protocols.filter(
              (p) =>
                !p.isFork &&
                p.timestamp < protocol.timestamp &&
                p.timestamp + p.duration < protocol.timestamp &&
                protocol.timestamp - (p.timestamp + p.duration) < 10000, // Within 10 seconds
            );

            if (potentialParents.length > 0) {
              // Take the closest parent
              const parent = potentialParents.reduce((closest, p) => {
                const pEnd = p.timestamp + p.duration;
                const closestEnd = closest.timestamp + closest.duration;
                return Math.abs(protocol.timestamp - pEnd) <
                  Math.abs(protocol.timestamp - closestEnd)
                  ? p
                  : closest;
              });

              console.log(
                `Inferring parent for ${protocol.operationName}: ${parent.operationName}`,
              );
              return {
                ...protocol,
                parentTransactionId: parent.transactionId || parent.id,
              };
            }
          }

          return protocol;
        });

        // Update the store
        useDebugProtocolStore.setState({ protocols: updated });
        console.log(
          "Inferred relationships for",
          updated.filter((p) => p.parentTransactionId).length,
          "protocols",
        );
      },
      // Analyze protocol relationships and timing
      analyzeProtocols: () => {
        const state = useDebugProtocolStore.getState();
        const protocols = state.protocols;

        // Group by approximate time (within 10 seconds)
        // biome-ignore lint/suspicious/noExplicitAny: legacy code
        const groups: any[] = [];
        for (const p of protocols) {
          let added = false;
          for (const group of groups) {
            // biome-ignore lint/suspicious/noExplicitAny: legacy code
            const groupStart = Math.min(...group.map((g: any) => g.timestamp));
            const groupEnd = Math.max(
              // biome-ignore lint/suspicious/noExplicitAny: legacy code
              ...group.map((g: any) => g.timestamp + g.duration),
            );

            if (
              p.timestamp >= groupStart - 10000 &&
              p.timestamp <= groupEnd + 10000
            ) {
              group.push(p);
              added = true;
              break;
            }
          }
          if (!added) {
            groups.push([p]);
          }
        }

        console.log("Protocol groups:", groups);

        // Analyze each group
        groups.forEach((group, i) => {
          console.log(`\nGroup ${i}:`);

          // Sort by timestamp
          // biome-ignore lint/suspicious/noExplicitAny: legacy code
          group.sort((a: any, b: any) => a.timestamp - b.timestamp);

          // Find potential parent-fork relationships
          // biome-ignore lint/suspicious/noExplicitAny: legacy code
          const nonForks = group.filter((p: any) => !p.isFork);
          // biome-ignore lint/suspicious/noExplicitAny: legacy code
          const forks = group.filter((p: any) => p.isFork);

          console.log(
            "Non-forks:",
            // biome-ignore lint/suspicious/noExplicitAny: legacy code
            nonForks.map((p: any) => ({
              name: p.operationName,
              time: p.timestamp,
              duration: p.duration,
              end: p.timestamp + p.duration,
            })),
          );

          console.log(
            "Forks:",
            // biome-ignore lint/suspicious/noExplicitAny: legacy code
            forks.map((p: any) => ({
              name: p.operationName,
              time: p.timestamp,
              parentId: p.parentTransactionId,
            })),
          );

          // Check for gaps
          if (nonForks.length > 0 && forks.length > 0) {
            const lastNonForkEnd = Math.max(
              // biome-ignore lint/suspicious/noExplicitAny: legacy code
              ...nonForks.map((p: any) => p.timestamp + p.duration),
            );
            const firstForkStart = Math.min(
              // biome-ignore lint/suspicious/noExplicitAny: legacy code
              ...forks.map((p: any) => p.timestamp),
            );
            const gap = firstForkStart - lastNonForkEnd;

            console.log("Gap between non-fork and fork:", gap, "ms");
          }
        });
      },
      // Reprocess all protocols to fix error detection
      reprocessErrors: () => {
        const state = useDebugProtocolStore.getState();
        const updatedProtocols = state.protocols.map((p) => {
          // Skip if protocol already has a real error from the server
          if (p.raw?.error) {
            return p;
          }

          // Re-check for errors in debug entries
          let errorMessage = "";
          const entries = p.entries || [];

          if (entries.length > 0) {
            // biome-ignore lint/suspicious/noExplicitAny: legacy code
            const errorEntry = entries.find((e: any) => {
              if (e.type !== EntryType.DebugInfo || !e.debugInfos) return false;

              // Check if it's actually an error entry
              const hasErrorType =
                e.debugInfos.errorType &&
                e.debugInfos.errorType !== "Permission";
              const hasErrorCode = typeof e.debugInfos.errorCode === "string";
              const hasErrorStack = e.debugInfos.stack?.includes("Error");

              // If extraInfo is empty and we have error indicators, it's likely an error
              const isLikelyError =
                (e.extraInfo === "" ||
                  e.extraInfo === null ||
                  e.extraInfo === undefined) &&
                (hasErrorType || hasErrorCode || hasErrorStack);

              return hasErrorType || hasErrorCode || isLikelyError;
            });

            if (errorEntry) {
              // Extract error message from debug infos
              if (errorEntry.extraInfo?.trim()) {
                errorMessage = errorEntry.extraInfo;
              } else if (errorEntry.debugInfos.stack) {
                // Parse error from stack trace
                const stackLines = errorEntry.debugInfos.stack.split("\\n");
                const firstLine = stackLines[0] || "";
                // Extract error message from first line of stack
                const match = firstLine.match(/^(\\w+Error|Error):\\s*(.*)$/);
                if (match) {
                  const errorType = match[1];
                  const message = match[2]?.trim() || "";
                  errorMessage = message
                    ? `${errorType}: ${message}`
                    : `${errorType} at ${p.operationName}`;
                } else {
                  errorMessage =
                    errorEntry.debugInfos.errorType || "Error occurred";
                }
              } else if (errorEntry.debugInfos.errorType) {
                errorMessage = `${errorEntry.debugInfos.errorType}: ${errorEntry.debugInfos.message || "Unknown error"}`;
              } else {
                errorMessage = "Error occurred (check protocol details)";
              }
            }
          }

          // Update the protocol with corrected error status
          return { ...p, error: errorMessage };
        });

        // Update the store
        useDebugProtocolStore.setState({ protocols: updatedProtocols });

        const errorCount = updatedProtocols.filter((p) => p.error).length;
        console.log(
          `Reprocessed ${updatedProtocols.length} protocols, found ${errorCount} actual errors`,
        );
      },
      // Debug specific protocol
      debug: (id: string) => {
        const protocol = protocols.find(
          (p) => p.id === id || p.transactionId === id,
        );
        if (!protocol) {
          console.log("Protocol not found");
          return;
        }
        console.log("Protocol:", protocol);
        console.log("Is Fork:", protocol.isFork);
        console.log("Parent Transaction ID:", protocol.parentTransactionId);
        console.log("Transaction ID:", protocol.transactionId);
        console.log("Entries:", protocol.entries);
        if (protocol.parentTransactionId) {
          const parent = protocols.find(
            (p) => (p.transactionId || p.id) === protocol.parentTransactionId,
          );
          console.log("Parent Protocol:", parent);
        }
      },
    };

    // Cleanup function to remove global objects when component unmounts
    return () => {
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      delete (globalThis as any).debugProtocol;
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      delete (globalThis as any).debugProtocolStore;
    };
  }, [data?.debugProtocolSubscription, protocols, clearProtocols]);

  if (error) {
    console.error(error);
  }

  useEffect(() => {
    localStorage.setItem(
      "debug_enableDataLogging",
      enableDataLogging.toString(),
    );
  }, [enableDataLogging]);

  useEffect(() => {
    localStorage.setItem(
      "debug_enableContextPicker",
      enableContextPicker.toString(),
    );
  }, [enableContextPicker]);

  useEffect(() => {
    localStorage.setItem(
      "debug_enableStoreInConsole",
      enableStoreInConsole.toString(),
    );
  }, [enableStoreInConsole]);

  useEffect(() => {
    localStorage.setItem("graphqlDebug", enableGraphQLDebug.toString());
  }, [enableGraphQLDebug]);

  useEffect(() => {
    localStorage.setItem("debug_viewMode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (enableStoreInConsole) {
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      (globalThis as any).as = atomStore;
    } else {
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      (globalThis as any).as = undefined;
    }
  }, [enableStoreInConsole, atomStore]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Toggle debug menu on Ctrl+Shift+D
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === "d") {
        event.preventDefault();
        setVisible((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Use useMemo to ensure the context value only changes when the state values change
  const debugSettingsValue = useMemo(
    () => ({
      enableDataLogging,
      enableContextPicker,
      setEnableDataLogging,
      setEnableContextPicker,
    }),
    [enableDataLogging, enableContextPicker],
  );

  return (
    <DebugSettingsContext.Provider value={debugSettingsValue}>
      {children}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: theme.background,
          color: theme.text,
          zIndex: 1000,
          display: visible ? "flex" : "none",
          flexDirection: "column",
          pointerEvents: visible ? "auto" : "none",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: theme.headerBg,
            borderBottom: `1px solid ${theme.borderColor}`,
            padding: "1rem 2rem",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h1 style={{ margin: 0, fontSize: "1.5rem", color: theme.text }}>
                Debug Console
              </h1>
              <p
                style={{
                  margin: "0.25rem 0 0 0",
                  fontSize: "0.875rem",
                  color: theme.subtextColor,
                }}
              >
                Press Ctrl+Shift+D to close
              </p>
            </div>

            {/* Control switches in header */}
            <div style={{ display: "flex", gap: "2rem", alignItems: "center" }}>
              {/* View mode toggle */}
              <button
                type="button"
                onClick={() =>
                  setViewMode((prev) =>
                    prev === "timeline" ? "list" : "timeline",
                  )
                }
                style={{
                  padding: "0.375rem 0.75rem",
                  background: theme.buttonBg,
                  color: theme.buttonText,
                  border: `1px solid ${theme.borderColor}`,
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
                title="Toggle view mode"
              >
                {viewMode === "timeline" ? "📋" : "📊"}{" "}
                {viewMode === "timeline" ? "List" : "Timeline"}
              </button>

              {/* Theme toggle */}
              <button
                type="button"
                onClick={() => setIsLightMode((prev) => !prev)}
                style={{
                  padding: "0.375rem 0.75rem",
                  background: theme.buttonBg,
                  color: theme.buttonText,
                  border: `1px solid ${theme.borderColor}`,
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
                title="Toggle theme"
              >
                {isLightMode ? "🌙" : "☀️"} {isLightMode ? "Dark" : "Light"}
              </button>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  cursor: "pointer",
                  color: theme.text,
                }}
              >
                <input
                  type="checkbox"
                  checked={enableDataLogging}
                  onChange={() => setEnableDataLogging((prev) => !prev)}
                  style={{ cursor: "pointer" }}
                />
                <span style={{ fontSize: "0.875rem" }}>Data Logging</span>
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  cursor: "pointer",
                  color: theme.text,
                }}
              >
                <input
                  type="checkbox"
                  checked={enableContextPicker}
                  onChange={() => setEnableContextPicker((prev) => !prev)}
                  style={{ cursor: "pointer" }}
                />
                <span style={{ fontSize: "0.875rem" }}>Context Picker</span>
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  cursor: "pointer",
                  color: theme.text,
                }}
              >
                <input
                  type="checkbox"
                  checked={enableStoreInConsole}
                  onChange={() => setEnableStoreInConsole((prev) => !prev)}
                  style={{ cursor: "pointer" }}
                />
                <span style={{ fontSize: "0.875rem" }}>Console Store</span>
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  cursor: "pointer",
                  color: theme.text,
                }}
              >
                <input
                  type="checkbox"
                  checked={enableGraphQLDebug}
                  onChange={() => setEnableGraphQLDebug((prev) => !prev)}
                  style={{ cursor: "pointer" }}
                />
                <span style={{ fontSize: "0.875rem" }}>GraphQL Debug</span>
              </label>

              <div
                style={{
                  borderLeft: `1px solid ${theme.borderColor}`,
                  paddingLeft: "2rem",
                  display: "flex",
                  gap: "1rem",
                  alignItems: "center",
                }}
              >
                <button
                  type="button"
                  onClick={clearProtocols}
                  style={{
                    padding: "0.375rem 1rem",
                    background: theme.errorColor,
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                  }}
                >
                  Clear All
                </button>

                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontSize: "0.875rem",
                    color: theme.text,
                  }}
                >
                  Max:
                  <input
                    type="number"
                    value={maxProtocols}
                    onChange={(e) => setMaxProtocols(Number(e.target.value))}
                    style={{
                      width: "50px",
                      padding: "0.25rem",
                      background: theme.inputBg,
                      color: theme.text,
                      border: `1px solid ${theme.inputBorder}`,
                      borderRadius: "4px",
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div
          style={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            padding: "1rem 2rem",
            gap: "1rem",
          }}
        >
          {/* Timeline or List View */}
          {viewMode === "timeline" ? (
            <TimelineOverview
              protocols={protocols}
              selectedProtocolId={selectedProtocolId}
              onSelectProtocol={(protocol) =>
                setSelectedProtocolId(protocol.id)
              }
              theme={theme}
            />
          ) : (
            <ProtocolList
              protocols={protocols}
              selectedProtocolId={selectedProtocolId}
              onSelectProtocol={(protocol) =>
                setSelectedProtocolId(protocol.id)
              }
              theme={theme}
            />
          )}

          {/* Selected protocol detail */}
          {selectedProtocolId &&
            (() => {
              const selectedProtocol = protocols.find(
                (p) => p.id === selectedProtocolId,
              );
              if (!selectedProtocol) return null;

              return (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    background: theme.cardBg,
                    border: `1px solid ${theme.borderColor}`,
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: "0.75rem 1rem",
                      borderBottom: `1px solid ${theme.borderColor}`,
                      display: "flex",
                      flexDirection: "column",
                      background: theme.cardHeaderBg,
                      border: `1px solid ${theme.borderColor}`,
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "1rem",
                        alignItems: "center",
                      }}
                    >
                      <h3
                        style={{
                          margin: 0,
                          fontSize: "1rem",
                          color: theme.text,
                        }}
                      >
                        Protocol Details
                      </h3>
                      {selectedProtocol.isFork && (
                        <span
                          style={{
                            background: theme.purpleColor,
                            color: "white",
                            padding: "0.125rem 0.5rem",
                            borderRadius: "4px",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                          }}
                        >
                          FORK
                        </span>
                      )}
                      {selectedProtocol.error && (
                        <span
                          style={{
                            background: theme.errorColor,
                            color: "white",
                            padding: "0.125rem 0.5rem",
                            borderRadius: "4px",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                          }}
                        >
                          ERROR
                        </span>
                      )}
                      <span style={{ fontWeight: "500", color: theme.text }}>
                        {selectedProtocol.operationName}
                      </span>
                      <span
                        style={{
                          fontSize: "0.875rem",
                          color: theme.subtextColor,
                        }}
                      >
                        {new Date(
                          selectedProtocol.timestamp,
                        ).toLocaleTimeString()}
                      </span>
                      <span
                        style={{
                          fontSize: "0.875rem",
                          color: selectedProtocol.error
                            ? theme.errorColor
                            : selectedProtocol.duration > 100
                              ? theme.warningColor
                              : theme.successColor,
                          fontWeight: "500",
                        }}
                      >
                        {selectedProtocol.duration}ms
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button
                        type="button"
                        onClick={() => setSelectedProtocolId(undefined)}
                        style={{
                          padding: "0.25rem 0.75rem",
                          background: theme.buttonBg,
                          color: theme.buttonText,
                          border: `1px solid ${theme.borderColor}`,
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "0.75rem",
                        }}
                      >
                        Close
                      </button>
                      <RemoveButton
                        onClick={() => {
                          removeProtocol(selectedProtocol.id);
                          setSelectedProtocolId(undefined);
                        }}
                        theme={theme}
                      />
                    </div>
                  </div>
                  <div style={{ flex: 1, overflowY: "auto", padding: "1rem" }}>
                    <EnhancedProtocolViewer
                      protocol={selectedProtocol.entries}
                      error={selectedProtocol.error}
                      theme={theme}
                    />
                  </div>
                </div>
              );
            })()}
        </div>
      </div>
    </DebugSettingsContext.Provider>
  );
};
